import { useEffect, useRef, useState } from "react";
import type { ProductMedia } from "@/lib/api-client";

export interface GalleryProps {
  media: ProductMedia[];
  productName: string;
}

/** Défilement automatique des photos (hors survol) : §demande produit — 6 s entre chaque image. */
const AUTO_ADVANCE_MS = 6000;

/**
 * Porté à l'identique depuis apps/web/components/product/Gallery.tsx (aucune
 * dépendance Next.js dans l'original — `"use client"` retiré, pas de
 * pendant en mode SPA). Support vidéo (autoplay muet en boucle) conservé tel
 * quel.
 *
 * Ajouts : léger zoom au survol (`group-hover:scale-105`) et, s'il y a
 * plusieurs médias, défilement automatique toutes les 6 s — mis en pause
 * pendant le survol (sinon l'image changerait sous le curseur pendant que
 * l'utilisateur l'examine en zoom, contre-productif avec l'effet de survol).
 */
export function Gallery({ media, productName }: GalleryProps) {
  const sorted = [...media].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.position - b.position;
  });
  const [activeId, setActiveId] = useState<string | null>(sorted[0]?.id ?? null);
  const [isHovering, setIsHovering] = useState(false);
  const active = sorted.find((m) => m.id === activeId) ?? sorted[0] ?? null;

  const isVideoActive = active?.type === "VIDEO";

  useEffect(() => {
    // Pas de défilement automatique pendant une vidéo : le visiteur la regarde.
    if (sorted.length < 2 || isHovering || isVideoActive) return;
    const timer = setInterval(() => {
      setActiveId((current) => {
        const currentIndex = sorted.findIndex((m) => m.id === current);
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % sorted.length;
        return sorted[nextIndex]?.id ?? current;
      });
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sorted` est recréé à chaque rendu (tri d'un nouveau tableau) ; seule sa LONGUEUR doit relancer l'effet.
  }, [sorted.length, isHovering, isVideoActive]);

  if (!active) {
    return (
      <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-sm border border-line bg-cream2 p-6 text-center">
        <span aria-hidden="true" className="text-5xl text-line">
          {"\u{1F4E6}"}
        </span>
        <p className="font-body text-sm font-medium text-muted">{productName}</p>
        <p className="font-body text-xs text-muted">Photo à venir</p>
      </div>
    );
  }

  return (
    <div>
      <div
        className="group aspect-square w-full overflow-hidden rounded-sm border border-line bg-cream2"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {active.type === "VIDEO" ? (
          <InViewVideo key={active.id} src={active.url} />
        ) : (
          <img
            src={active.url}
            alt={active.alt ?? productName}
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
          />
        )}
      </div>

      {sorted.length > 1 ? (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {sorted.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveId(m.id)}
              aria-current={m.id === active.id}
              aria-label={m.type === "VIDEO" ? "Voir la vidéo" : `Voir la photo ${m.position + 1}`}
              className={[
                "relative h-16 w-16 shrink-0 overflow-hidden rounded-sm border-2",
                m.id === active.id ? "border-navy" : "border-line",
              ].join(" ")}
            >
              {m.type === "VIDEO" ? (
                <>
                  <video src={m.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center bg-navy/30 text-sm text-cream"
                  >
                    {"▶"}
                  </span>
                </>
              ) : (
                <img src={m.url} alt="" className="h-full w-full object-cover" />
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Vidéo produit en lecture automatique UNIQUEMENT quand elle est visible à l'écran (au
 * moins la moitié dans le viewport) : lancée quand le visiteur fait défiler jusqu'à la
 * galerie, mise en pause quand il la quitte (économise batterie et data mobile). Muette
 * (condition imposée par les navigateurs pour l'autoplay) ; `controls` pour activer le son.
 */
function InViewVideo({ src }: { src: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (typeof IntersectionObserver === "undefined") {
      void video.play().catch(() => undefined);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          // play() peut être refusé (économie d'énergie iOS…) : les contrôles restent disponibles.
          void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      muted
      loop
      playsInline
      controls
      preload="metadata"
      className="h-full w-full bg-ink object-contain"
    />
  );
}
